import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { CloudArrowUp, FileText, X, Sparkle } from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";
import { trackAddToCart } from "../../lib/pixel";

export default function StepUpload({ onParsed }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25MB)");
      return;
    }
    if (!(f.type.startsWith("image/") || f.type === "application/pdf")) {
      toast.error("Please upload a PDF or image");
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/bills/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      trackAddToCart(data);
      toast.success("Bill parsed!");
      onParsed(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not parse the bill. Please try a clearer image.";
      toast.error(msg);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="step-upload">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="inline-flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-full px-4 py-1.5 mb-5">
          <Sparkle size={14} weight="fill" className="text-sky-600" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-sky-700">
            Step 1 — AI bill parser
          </span>
        </div>
        <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight">
          Upload your electric bill
        </h2>
        <p className="mt-4 text-slate-600 text-lg">
          PDF or photo from any month. We'll auto-extract your usage, rate, and address —
          you can correct anything before continuing.
        </p>
      </div>

      <div
        data-testid="bill-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !file && inputRef.current?.click()}
        className={`max-w-2xl mx-auto border-2 border-dashed rounded-3xl p-12 text-center cursor-pointer transition-all ${
          dragOver
            ? "border-sky-500 bg-sky-50 scale-[1.01]"
            : file
            ? "border-emerald-500 bg-emerald-50/40"
            : "border-slate-300 bg-white hover:border-sky-400 hover:bg-sky-50/50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="bill-file-input"
        />
        {file ? (
          <div className="flex items-center justify-between gap-4 bg-white rounded-2xl p-5 shadow-sm">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <FileText size={24} weight="duotone" className="text-emerald-600" />
              </div>
              <div className="text-left min-w-0">
                <div className="font-semibold text-slate-900 truncate">{file.name}</div>
                <div className="text-sm text-slate-500">
                  {(file.size / 1024).toFixed(0)} KB &middot; {file.type}
                </div>
              </div>
            </div>
            <button
              data-testid="remove-file-btn"
              onClick={(e) => { e.stopPropagation(); setFile(null); }}
              className="p-2 hover:bg-slate-100 rounded-full"
            >
              <X size={18} className="text-slate-500" />
            </button>
          </div>
        ) : (
          <>
            <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-5">
              <CloudArrowUp size={36} weight="duotone" className="text-white" />
            </div>
            <h3 className="font-display text-2xl font-semibold text-slate-900">
              Drop your bill here
            </h3>
            <p className="text-slate-500 mt-2">or click to browse — PDF, PNG, JPG up to 25MB</p>
          </>
        )}
      </div>

      {file && (
        <div className="mt-8 flex justify-center">
          <button
            data-testid="bill-upload-btn"
            onClick={upload}
            disabled={loading}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? "Analyzing with AI..." : "Continue with AI analysis"}
          </button>
        </div>
      )}

      <div className="text-center mt-8 text-sm text-slate-500">
        Your bill is processed via Claude vision and never stored beyond extraction.
      </div>
    </div>
  );
}
